import { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '../../../../core/components/Button';
import { ProjectSelector } from './ProjectSelector';
import { UnitSelector } from './UnitSelector';
import { BannerSelector } from './BannerSelector';
import { AreaSelector } from './AreaSelector';
import { CategorySelector } from './CategorySelector';
import { ImageUpload } from '../../../../components/common/ImageUpload';
import { IconPicker } from '../../../../components/common/IconPicker';
import { useAdminT } from '../../../../hooks/useAdminT';
import * as Icons from 'lucide-react';

interface SectionConfigDialogProps {
    isOpen: boolean;
    onClose: () => void;
    section: any;
    onSave: (config: any) => void;
}

const inputClass = "w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none";
const labelClass = "block text-xs font-semibold text-slate-500 uppercase mb-1";

export const SectionConfigDialog: React.FC<SectionConfigDialogProps> = ({ isOpen, onClose, section, onSave }) => {
    const { t } = useAdminT();
    const [config, setConfig] = useState<any>({});

    useEffect(() => {
        if (isOpen && section) {
            setConfig(section.config || {});
        }
    }, [isOpen, section]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave(config);
        onClose();
    };

    const updateConfig = (key: string, value: any) => {
        setConfig((prev: any) => ({ ...prev, [key]: value }));
    };

    const updateTranslation = (field: string, lang: 'en' | 'ar', value: string) => {
        setConfig((prev: any) => ({
            ...prev,
            [field]: { ...(prev[field] || {}), [lang]: value }
        }));
    };

    const getTranslation = (field: string, lang: 'en' | 'ar') => {
        if (typeof config[field] === 'string') return lang === 'en' ? config[field] : '';
        return config[field]?.[lang] || '';
    };

    const renderTypeSpecificContent = () => {
        switch (section.type) {
            case 'featured_projects':
                return (
                    <div className="space-y-4 border-t pt-4">
                        <h4 className="font-bold text-sm text-slate-700">{t('pageBuilder.config.selectProjects')}</h4>
                        <ProjectSelector
                            selectedIds={config.itemIds || []}
                            onChange={(ids) => updateConfig('itemIds', ids)}
                        />

                        <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                            <h4 className="font-bold text-sm text-slate-700">{t('pageBuilder.config.sectionBackground')}</h4>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="bgMode"
                                        checked={!config.useCustomBackground}
                                        onChange={() => updateConfig('useCustomBackground', false)}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>{t('pageBuilder.config.useFirstProjectImage')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="bgMode"
                                        checked={!!config.useCustomBackground}
                                        onChange={() => updateConfig('useCustomBackground', true)}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>{t('pageBuilder.config.useCustomBackground')}</span>
                                </label>
                            </div>
                            {config.useCustomBackground && (
                                <ImageUpload
                                    value={config.backgroundImage}
                                    onChange={(url) => updateConfig('backgroundImage', url)}
                                    label={t('pageBuilder.config.customBgImage')}
                                />
                            )}
                        </div>

                        <div>
                            <label className={labelClass}>{t('pageBuilder.config.ctaUrl')}</label>
                            <input
                                type="text"
                                value={config.ctaUrl || ''}
                                onChange={(e) => updateConfig('ctaUrl', e.target.value)}
                                className={inputClass}
                                placeholder="/explore, /contact, etc."
                            />
                        </div>
                    </div>
                );

            case 'hero_slider':
                return (
                    <div className="space-y-4 border-t pt-4">
                        <div>
                            <h4 className="font-bold text-sm text-slate-700 mb-2">{t('pageBuilder.config.selectBanners')}</h4>
                            <p className="text-xs text-slate-500 mb-3">{t('pageBuilder.config.selectBannersHint')}</p>
                            <BannerSelector
                                selectedIds={config.itemIds || []}
                                onChange={(ids) => updateConfig('itemIds', ids)}
                            />
                        </div>
                    </div>
                );

            case 'locations_section':
                return (
                    <div className="space-y-4 border-t pt-4">
                        <div>
                            <h4 className="font-bold text-sm text-slate-700 mb-2">{t('pageBuilder.config.selectLocations')}</h4>
                            <p className="text-xs text-slate-500 mb-3">{t('pageBuilder.config.selectLocationsHint')}</p>
                            <AreaSelector
                                selectedIds={config.itemIds || []}
                                onChange={(ids) => updateConfig('itemIds', ids)}
                            />
                        </div>
                    </div>
                );

            case 'featured_units':
                return (
                    <div className="space-y-4 border-t pt-4">
                        <h4 className="font-bold text-sm text-slate-700">{t('pageBuilder.config.selectUnits')}</h4>
                        <UnitSelector
                            selectedIds={config.itemIds || []}
                            onChange={(ids) => updateConfig('itemIds', ids)}
                        />

                        <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                            <h4 className="font-bold text-sm text-slate-700">{t('pageBuilder.config.sectionBackground')}</h4>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="bgMode_units"
                                        checked={!config.useCustomBackground}
                                        onChange={() => updateConfig('useCustomBackground', false)}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>{t('pageBuilder.config.useFirstUnitImage')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="bgMode_units"
                                        checked={!!config.useCustomBackground}
                                        onChange={() => updateConfig('useCustomBackground', true)}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>{t('pageBuilder.config.useCustomBackground')}</span>
                                </label>
                            </div>
                            {config.useCustomBackground && (
                                <ImageUpload
                                    value={config.backgroundImage}
                                    onChange={(url) => updateConfig('backgroundImage', url)}
                                    label={t('pageBuilder.config.customBgImage')}
                                />
                            )}
                        </div>

                        <div>
                            <label className={labelClass}>{t('pageBuilder.config.ctaUrl')}</label>
                            <input
                                type="text"
                                value={config.ctaUrl || ''}
                                onChange={(e) => updateConfig('ctaUrl', e.target.value)}
                                className={inputClass}
                                placeholder="/explore, /contact, etc."
                            />
                        </div>
                    </div>
                );

            case 'discovery_header': {
                const categoryIds = config.categories || [];
                const moods = config.moods || [
                    { id: 'ready', label: { en: 'Ready to Move', ar: 'جاهز للاستلام' }, link: '' },
                    { id: 'investment', label: { en: 'Investment Gems', ar: 'فرص استثمارية' }, link: '' },
                ];

                const updateList = (field: string, newList: any[]) => updateConfig(field, newList);

                return (
                    <div className="space-y-8 border-t pt-6">
                        {/* Categories List */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h4 className="font-bold text-sm text-slate-700">{t('pageBuilder.config.categories')} (Property Types)</h4>
                                    <p className="text-xs text-slate-500 mt-1">Select the main property type tabs shown in the Explore header.</p>
                                </div>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                <CategorySelector
                                    selectedIds={categoryIds}
                                    onChange={(ids) => updateConfig('categories', ids)}
                                />
                            </div>
                        </div>

                        {/* Moods List */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h4 className="font-bold text-sm text-slate-700">{t('pageBuilder.config.moods')} (Filter Chips)</h4>
                                    <p className="text-xs text-slate-500 mt-1">Configure the Quick Filter chips shown below the categories.</p>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => updateList('moods', [...moods, { id: `mood-${Date.now()}`, label: { en: 'New Filter', ar: 'فلتر جديد' }, link: '' }])}>
                                    + {t('common.add')} Filter
                                </Button>
                            </div>
                            <div className="space-y-4">
                                {moods.map((mood: any, idx: number) => (
                                    <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200 relative group">
                                        <button className="absolute -top-3 -right-3 bg-red-100 text-red-600 hover:bg-red-500 hover:text-white p-1.5 rounded-full shadow-sm transition-colors opacity-0 group-hover:opacity-100" onClick={() => {
                                            const newList = [...moods];
                                            newList.splice(idx, 1);
                                            updateList('moods', newList);
                                        }} title="Remove Filter">
                                            <X className="w-4 h-4" />
                                        </button>

                                        <div className="mb-3">
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">System ID</label>
                                            <input
                                                className="w-full md:w-1/2 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                                                placeholder="e.g. ready_to_move"
                                                value={mood.id || ''}
                                                onChange={(e) => {
                                                    const newList = [...moods];
                                                    newList[idx].id = e.target.value;
                                                    updateList('moods', newList);
                                                }}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Display Label (English)</label>
                                                <input
                                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                                                    placeholder="e.g. Ready to Move"
                                                    value={mood.label?.en || ''}
                                                    onChange={(e) => {
                                                        const newList = [...moods];
                                                        newList[idx].label = { ...(newList[idx].label || {}), en: e.target.value };
                                                        updateList('moods', newList);
                                                    }}
                                                />
                                            </div>
                                            <div dir="rtl">
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 text-right">Display Label (Arabic)</label>
                                                <input
                                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none text-right"
                                                    placeholder="مثال: استلام فوري"
                                                    value={mood.label?.ar || ''}
                                                    onChange={(e) => {
                                                        const newList = [...moods];
                                                        newList[idx].label = { ...(newList[idx].label || {}), ar: e.target.value };
                                                        updateList('moods', newList);
                                                    }}
                                                    dir="rtl"
                                                />
                                            </div>
                                        </div>

                                        <div className="mt-3 pt-3 border-t border-slate-200">
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Explicit Search Query (For filtering)</label>
                                            <input
                                                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none font-mono text-slate-600 bg-white"
                                                placeholder="/search?ready=true&sort=priceAsc"
                                                value={mood.link || ''}
                                                onChange={(e) => {
                                                    const newList = [...moods];
                                                    newList[idx].link = e.target.value;
                                                    updateList('moods', newList);
                                                }}
                                            />
                                            <p className="text-[10px] text-slate-400 mt-1">If empty, defaults to auto-generating ?collection=ID</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            }

            case 'trust_section': {
                const items = config.items || [
                    { id: '1', icon: 'Shield', title: { en: 'Verified Developer', ar: 'مطور معتمد' }, description: { en: 'Guaranteed success', ar: 'نجاح مضمون' } },
                    { id: '2', icon: 'Award', title: { en: 'Premium Quality', ar: 'جودة عالية' }, description: { en: 'Best materials', ar: 'أفضل المواد' } },
                    { id: '3', icon: 'Clock', title: { en: 'On Time Delivery', ar: 'تسليم في الموعد' }, description: { en: 'No delays', ar: 'بدون تأخير' } },
                ];

                const updateList = (newList: any[]) => updateConfig('items', newList);

                return (
                    <div className="space-y-6 border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h4 className="font-bold text-sm text-slate-700">{t('pageBuilder.config.trustItems', 'Trust Items')}</h4>
                                <p className="text-xs text-slate-500 mt-1">Configure the 3 guarantees/trust items shown in this section.</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {items.map((item: any, idx: number) => {
                                const Icon = item.icon && (Icons as any)[item.icon] ? (Icons as any)[item.icon] : Icons.Shield;
                                return (
                                    <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200 relative group space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Icon Select</label>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center text-primary shrink-0 relative z-10">
                                                        <Icon size={20} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <IconPicker
                                                            value={item.icon || 'Shield'}
                                                            onChange={(val) => {
                                                                const newList = [...items];
                                                                newList[idx].icon = val;
                                                                updateList(newList);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Title (English)</label>
                                                <input
                                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                                                    value={item.title?.en || ''}
                                                    onChange={(e) => {
                                                        const newList = [...items];
                                                        newList[idx].title = { ...(newList[idx].title || {}), en: e.target.value };
                                                        updateList(newList);
                                                    }}
                                                />
                                            </div>
                                            <div dir="rtl">
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 text-right">Title (Arabic)</label>
                                                <input
                                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none text-right"
                                                    value={item.title?.ar || ''}
                                                    onChange={(e) => {
                                                        const newList = [...items];
                                                        newList[idx].title = { ...(newList[idx].title || {}), ar: e.target.value };
                                                        updateList(newList);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Description (English)</label>
                                                <textarea
                                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                                                    value={item.description?.en || ''}
                                                    rows={2}
                                                    onChange={(e) => {
                                                        const newList = [...items];
                                                        newList[idx].description = { ...(newList[idx].description || {}), en: e.target.value };
                                                        updateList(newList);
                                                    }}
                                                />
                                            </div>
                                            <div dir="rtl">
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 text-right">Description (Arabic)</label>
                                                <textarea
                                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none text-right"
                                                    value={item.description?.ar || ''}
                                                    rows={2}
                                                    onChange={(e) => {
                                                        const newList = [...items];
                                                        newList[idx].description = { ...(newList[idx].description || {}), ar: e.target.value };
                                                        updateList(newList);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                );
            }

            case 'expert_pick':
                return (
                    <div className="space-y-6 border-t pt-6">
                        <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100 space-y-4">
                            <h4 className="font-bold text-sm text-amber-900 mb-2">{t('pageBuilder.config.expertPickSettings', 'Expert Pick Styling & Content')}</h4>

                            {/* Background Image */}
                            <div className="mb-4">
                                <label className={labelClass}>{t('pageBuilder.config.backgroundImage', 'Stunning Background Image')}</label>
                                <ImageUpload
                                    value={config.backgroundImage}
                                    onChange={(url) => updateConfig('backgroundImage', url)}
                                />
                                <p className="text-[10px] text-slate-500 mt-1">Use a high-resolution, wide-aspect image for maximum luxury impact.</p>
                            </div>

                            {/* Badge & Subtitle */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Badge Text (EN)</label>
                                    <input className={inputClass} placeholder="e.g. Expert Pick" value={getTranslation('badge', 'en')} onChange={(e) => updateTranslation('badge', 'en', e.target.value)} />
                                </div>
                                <div dir="rtl">
                                    <label className={labelClass}>Badge Text (AR)</label>
                                    <input className={inputClass} placeholder="مثال: اختيار الخبراء" value={getTranslation('badge', 'ar')} onChange={(e) => updateTranslation('badge', 'ar', e.target.value)} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Subtitle (EN)</label>
                                    <input className={inputClass} placeholder="e.g. Deal of the Month" value={getTranslation('subtitle', 'en')} onChange={(e) => updateTranslation('subtitle', 'en', e.target.value)} />
                                </div>
                                <div dir="rtl">
                                    <label className={labelClass}>Subtitle (AR)</label>
                                    <input className={inputClass} placeholder="مثال: صفقة الشهر" value={getTranslation('subtitle', 'ar')} onChange={(e) => updateTranslation('subtitle', 'ar', e.target.value)} />
                                </div>
                            </div>

                            {/* Features (Price & Payment) */}
                            <div className="grid grid-cols-2 gap-4 border-t border-amber-200/50 pt-4">
                                <div>
                                    <label className={labelClass}>Feature 1 / Price (EN)</label>
                                    <input className={inputClass} placeholder="e.g. Starting from 12M EGP" value={getTranslation('feature1', 'en')} onChange={(e) => updateTranslation('feature1', 'en', e.target.value)} />
                                </div>
                                <div dir="rtl">
                                    <label className={labelClass}>Feature 1 / Price (AR)</label>
                                    <input className={inputClass} placeholder="مثال: يبدأ من 12 مليون جنيه" value={getTranslation('feature1', 'ar')} onChange={(e) => updateTranslation('feature1', 'ar', e.target.value)} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Feature 2 / Payment (EN)</label>
                                    <input className={inputClass} placeholder="e.g. Limited Time Payment Plan" value={getTranslation('feature2', 'en')} onChange={(e) => updateTranslation('feature2', 'en', e.target.value)} />
                                </div>
                                <div dir="rtl">
                                    <label className={labelClass}>Feature 2 / Payment (AR)</label>
                                    <input className={inputClass} placeholder="مثال: خطة سداد لفترة محدودة" value={getTranslation('feature2', 'ar')} onChange={(e) => updateTranslation('feature2', 'ar', e.target.value)} />
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="grid grid-cols-2 gap-4 border-t border-amber-200/50 pt-4">
                                <div>
                                    <label className={labelClass}>Button CTA (EN)</label>
                                    <input className={inputClass} placeholder="e.g. Request Floor Plan & Prices" value={getTranslation('ctaText', 'en')} onChange={(e) => updateTranslation('ctaText', 'en', e.target.value)} />
                                </div>
                                <div dir="rtl">
                                    <label className={labelClass}>Button CTA (AR)</label>
                                    <input className={inputClass} placeholder="مثال: اطلب المخططات والأسعار" value={getTranslation('ctaText', 'ar')} onChange={(e) => updateTranslation('ctaText', 'ar', e.target.value)} />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Button Link URL</label>
                                <input className={inputClass} placeholder="e.g. /search?type=villa" value={config.ctaUrl || ''} onChange={(e) => updateConfig('ctaUrl', e.target.value)} />
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="mt-4 bg-slate-50 p-4 rounded-lg">
                        <h4 className="font-bold text-sm text-slate-700 mb-2">{t('pageBuilder.config.sectionBackground')}</h4>
                        <ImageUpload
                            value={config.backgroundImage}
                            onChange={(url) => updateConfig('backgroundImage', url)}
                            label={t('pageBuilder.config.customBgImage')}
                        />
                    </div>
                );
        }
    };

    const dialogTitle = section.label || (section.type
        ? section.type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        : '');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden text-slate-900">
                {/* Header */}
                <div className="p-4 border-b flex items-center justify-between bg-slate-50">
                    <h3 className="font-bold text-lg">{t('pageBuilder.config.title')}: {dialogTitle}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-200 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    {/* General Settings */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>{t('pageBuilder.config.titleEn')}</label>
                                <input
                                    type="text"
                                    value={getTranslation('title', 'en')}
                                    onChange={(e) => updateTranslation('title', 'en', e.target.value)}
                                    className={inputClass}
                                    placeholder="Section Title"
                                />
                            </div>
                            <div dir="rtl">
                                <label className={labelClass}>{t('pageBuilder.config.titleAr')}</label>
                                <input
                                    type="text"
                                    value={getTranslation('title', 'ar')}
                                    onChange={(e) => updateTranslation('title', 'ar', e.target.value)}
                                    className={inputClass}
                                    placeholder="عنوان القسم"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>{t('pageBuilder.config.descEn')}</label>
                                <textarea
                                    value={getTranslation('description', 'en')}
                                    onChange={(e) => updateTranslation('description', 'en', e.target.value)}
                                    className={`${inputClass} min-h-[80px]`}
                                    placeholder="Section Description"
                                />
                            </div>
                            <div dir="rtl">
                                <label className={labelClass}>{t('pageBuilder.config.descAr')}</label>
                                <textarea
                                    value={getTranslation('description', 'ar')}
                                    onChange={(e) => updateTranslation('description', 'ar', e.target.value)}
                                    className={`${inputClass} min-h-[80px]`}
                                    placeholder="وصف القسم"
                                />
                            </div>
                        </div>
                    </div>

                    {renderTypeSpecificContent()}
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-slate-50 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
                    <Button onClick={handleSave}>
                        <Save className="w-4 h-4 mr-2" />
                        {t('pageBuilder.config.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
